import LgpdNotice from './LgpdNotice';
import { openConsentPreferences } from '@/utils/consent';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

export default function Footer() {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const { ref: footerRef, isVisible: footerVisible } = useScrollAnimation({ threshold: 0.05 });

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const formData = new URLSearchParams();
      formData.append('email', email);

      const response = await fetch('https://ordem-de-servico.stibpalestrasilenciosasp.com.br/pedido.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Inscrição realizada com sucesso!');
        setEmail('');
      } else {
        setSubmitMessage('Erro ao realizar inscrição. Tente novamente.');
      }
    } catch (error) {
      setSubmitMessage('Erro ao realizar inscrição. Tente novamente.');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setSubmitMessage(''), 5000);
    }
  };

  return (
    <footer ref={footerRef} className="bg-[#1a1a1a] dark:bg-gray-950 text-white rounded-t-[32px] mt-20" role="contentinfo">
      <div className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 mb-16">
          {/* Coluna Esquerda */}
          <div className={`md:col-span-2 lg:col-span-1 transition-all duration-700 ${footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <Link to="/" aria-label="STIB - Palestra Silenciosa São Paulo - Página Inicial" className="inline-block hover:opacity-90 transition-opacity">
              <img
                src="/img/stib/logo-stib-3d.png"
                alt="STIB - Palestra Silenciosa São Paulo Logo"
                className="h-14 w-auto mb-6"
                loading="lazy"
              />
            </Link>
            <p className="font-body text-gray-300 dark:text-gray-400 text-base leading-relaxed mb-8 max-w-[280px]">
              <strong>Tecnologia inovadora</strong> de áudio sem fio para <strong>eventos corporativos</strong>, <strong>conferências multilíngues</strong> e workshops em <strong>São Paulo</strong>.
            </p>
            <form
              onSubmit={handleNewsletterSubmit}
              className="space-y-4"
              data-readdy-form
              id="newsletter-footer"
              aria-label="Formulário de inscrição na newsletter"
            >
              <label htmlFor="newsletter-email" className="sr-only">Seu e-mail para newsletter</label>
              <input
                type="email"
                name="email"
                id="newsletter-email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Seu e-mail"
                required
                className="w-full bg-transparent border-b border-primary dark:border-primary/80 text-white placeholder-gray-400 dark:placeholder-gray-500 py-2 focus:outline-none focus:border-primary font-body transition-colors"
              />
              <LgpdNotice kind="newsletter" className="mb-4 col-span-full" />
            <button
                type="submit"
                disabled={isSubmitting}
                className="bg-white dark:bg-gray-100 text-black dark:text-gray-900 px-8 py-3 rounded-full font-body font-semibold hover:bg-gray-100 dark:hover:bg-gray-200 transition-all flex items-center gap-3 whitespace-nowrap cursor-pointer disabled:opacity-50 hover:scale-105"
              >
                {isSubmitting ? 'Enviando...' : 'INSCREVER-SE'}
                <div className="w-6 h-6 flex items-center justify-center bg-black dark:bg-gray-900 rounded-full">
                  <i className="ri-arrow-right-line text-white text-sm" aria-hidden="true"></i>
                </div>
              </button>
              {submitMessage && (
                <p className={`text-sm ${submitMessage.includes('sucesso') ? 'text-primary' : 'text-red-400'}`} role="status">{submitMessage}</p>
              )}
            </form>
          </div>

          {/* Navegação */}
          <nav className={`transition-all duration-700 delay-100 ${footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} aria-label="Links de navegação do rodapé">
            <h4 className="font-heading text-sm text-primary dark:text-primary/90 uppercase mb-6 tracking-wide">
              <a href="#navegacao" className="hover:underline">NAVEGAÇÃO</a>
            </h4>
            <ul className="space-y-4">
              {[
                { to: '/', label: 'Home' },
                { to: '/o-que-fazemos', label: 'O Que Fazemos' },
                { to: '/portfolio', label: 'Portfólio' },
                { to: '/blog', label: 'Blog' },
                { to: '/quem-somos', label: 'Quem Somos' },
                { to: '/contato', label: 'Contato' },
              ].map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Locais de Eventos */}
          <nav className={`transition-all duration-700 delay-200 ${footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} aria-label="Locais de eventos parceiros">
            <h4 className="font-heading text-sm text-primary dark:text-primary/90 uppercase mb-6 tracking-wide">
              <a href="#locais" className="hover:underline">LOCAIS DE EVENTOS</a>
            </h4>
            <ul className="space-y-4">
              <li>
                <Link to="/locais" className="block font-body text-base text-primary hover:text-primary/80 transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block font-semibold">
                  Todos os Locais
                </Link>
              </li>
              <li className="pt-2">
                <span className="font-heading text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">São Paulo</span>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-transamerica-expo" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Transamerica Expo Center
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-expocenter-norte" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Expo Center Norte
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-frei-caneca" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Frei Caneca
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-distrito-anhembi" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Distrito Anhembi
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-wtc-events-center" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  WTC Events Center
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-villa-bisutti" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Villa Bisutti
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-tokio-marine-hall" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Tokio Marine Hall
                </Link>
              </li>
              <li className="pt-2">
                <span className="font-heading text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Rio de Janeiro</span>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-riocentro" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Riocentro
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-sulamerica-rj" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  SulAmérica
                </Link>
              </li>
              <li className="pt-2">
                <span className="font-heading text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Belo Horizonte</span>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-expominas-bh" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Expominas
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-minascentro-bh" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Minascentro
                </Link>
              </li>
              <li className="pt-2">
                <span className="font-heading text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Brasília</span>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-brasil-21" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Brasil 21
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-espaco-dois-ipes" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Espaço Dois Ipês
                </Link>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-arena-mane-garrincha" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Arena Mané Garrincha
                </Link>
              </li>
              <li className="pt-2">
                <span className="font-heading text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wider">Curitiba</span>
              </li>
              <li>
                <Link to="/locais/palestra-silenciosa-expotrade-curitiba" className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                  Expotrade
                </Link>
              </li>
            </ul>
          </nav>

          {/* Comparativos — oculto visualmente, mantido no DOM para SEO */}
          <nav className="sr-only" aria-label="Comparativos com concorrentes">
            <h4>COMPARATIVOS</h4>
            <ul>
              <li>
                <Link to="/comparativo">Todos os Comparativos</Link>
              </li>
              <li>
                <Link to="/comparativo/stib-vs-world-sound-translation">vs World Sound Translation</Link>
              </li>
              <li>
                <Link to="/comparativo/stib-vs-green-sound">vs Green Sound</Link>
              </li>
              <li>
                <Link to="/comparativo/stib-vs-palestra-silenciosa-sbj">vs Palestra Silenciosa SBJ</Link>
              </li>
              <li>
                <Link to="/comparativo/stib-vs-ab-traducoes">vs AB Traduções</Link>
              </li>
            </ul>
          </nav>

          {/* São Paulo */}
          <nav className={`transition-all duration-700 delay-300 ${footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} aria-label="Páginas do estado de São Paulo">
            <h4 className="font-heading text-sm text-primary dark:text-primary/90 uppercase mb-6 tracking-wide">
              <a href="#sao-paulo" className="hover:underline">SÃO PAULO</a>
            </h4>
            <ul className="space-y-4">
              <li>
                <Link to="/sao-paulo-estado" className="block font-body text-base text-primary hover:text-primary/80 transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block font-semibold">
                  Estado de São Paulo (Hub)
                </Link>
              </li>
              {[
                { to: '/sao-paulo', label: 'São Paulo (Capital)' },
                { to: '/campinas', label: 'Campinas' },
                { to: '/barueri', label: 'Barueri / Alphaville' },
                { to: '/sao-jose-dos-campos', label: 'São José dos Campos' },
                { to: '/ribeirao-preto', label: 'Ribeirão Preto' },
                { to: '/santos', label: 'Santos' },
                { to: '/guarulhos', label: 'Guarulhos' },
                { to: '/sao-bernardo-do-campo', label: 'São Bernardo do Campo' },
                { to: '/jundiai', label: 'Jundiaí' },
              ].map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Outras Cidades */}
          <nav className={`transition-all duration-700 delay-400 ${footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} aria-label="Páginas de outras cidades">
            <h4 className="font-heading text-sm text-primary dark:text-primary/90 uppercase mb-6 tracking-wide">
              <a href="#cidades" className="hover:underline">OUTRAS CIDADES</a>
            </h4>
            <ul className="space-y-4">
              {[
                { to: '/rio-de-janeiro', label: 'Rio de Janeiro' },
                { to: '/brasilia', label: 'Brasília' },
                { to: '/belo-horizonte', label: 'Belo Horizonte' },
                { to: '/curitiba', label: 'Curitiba' },
                { to: '/porto-alegre', label: 'Porto Alegre' },
                { to: '/recife', label: 'Recife' },
                { to: '/fortaleza', label: 'Fortaleza' },
                { to: '/salvador', label: 'Salvador' },
                { to: '/aracaju', label: 'Aracaju' },
                { to: '/natal', label: 'Natal' },
              ].map((item) => (
                <li key={item.to}>
                  <Link to={item.to} className="block font-body text-base text-white dark:text-gray-300 hover:text-primary transition-colors duration-200 cursor-pointer hover:translate-x-1 inline-block">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Contato */}
          <div className={`transition-all duration-700 delay-400 ${footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h4 className="font-heading text-sm text-primary dark:text-primary/90 uppercase mb-6 tracking-wide">
              <a href="#contato" className="hover:underline">CONTATO</a>
            </h4>
            <address className="space-y-4 mb-10 not-italic">
              <p className="font-body text-base text-white dark:text-gray-300">
                <i className="ri-phone-line mr-2" aria-hidden="true"></i>
                <a href="tel:+5511917776227" className="hover:text-primary transition-colors">(11) 91777-6227</a>
              </p>
              <p className="font-body text-base text-white dark:text-gray-300">
                <i className="ri-mail-line mr-2" aria-hidden="true"></i>
                <a href="mailto:contato@stibpalestrasilenciosasp.com.br" className="hover:text-primary transition-colors">contato@stibpalestrasilenciosasp.com.br</a>
              </p>
            </address>
            <h4 className="font-heading text-sm text-primary dark:text-primary/90 uppercase mb-6 tracking-wide">
              <a href="#localizacao" className="hover:underline">LOCALIZAÇÃO</a>
            </h4>
            <p className="font-body text-base text-gray-300 dark:text-gray-400 leading-relaxed">
              São Paulo, SP<br />Brasil
            </p>
          </div>
        </div>

        {/* Barra Inferior */}
        <div className={`border-t border-gray-700 dark:border-gray-800 pt-8 transition-all duration-700 delay-500 ${footerVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          <div className="flex flex-col md:flex-row justify-center items-center gap-4">
            <p className="font-body text-sm text-gray-400 dark:text-gray-500">
              © 2026 Palestra Silenciosa SP. Todos os direitos reservados.
            </p>
          <nav aria-label="Privacidade e termos" className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 font-body text-sm text-gray-400 dark:text-gray-500">
            <Link to="/politica-privacidade" className="hover:text-white hover:underline">Política de Privacidade</Link>
            <Link to="/politica-cookies" className="hover:text-white hover:underline">Política de Cookies</Link>
            <button type="button" onClick={openConsentPreferences} className="hover:text-white hover:underline cursor-pointer">Preferências de cookies</button>
          </nav>

            <nav aria-label="Redes Sociais STIB Palestra Silenciosa">
              <ul className="flex items-center gap-4">
                {[
                  { href: 'https://www.instagram.com/stibpalestrasilenciosa/', icon: 'ri-instagram-line', label: 'Instagram' },
                  { href: 'https://www.youtube.com/@stibpalestrasilenciosa', icon: 'ri-youtube-line', label: 'YouTube' },
                  { href: 'https://www.google.com/search?sca_esv=e50534bbba227543&sxsrf=APpeQntaAiC-jxjNpS1EXZKc4SLlK3eeYg%3A1789472341739&q=STIB%20Tradu%C3%A7%C3%A3o%20Simult%C3%A2nea%20e%20Palestra%20Silenciosa&stick=H4sIAAAAAAAAAONgU1I1qLA0SU41NU8yTzUytzBKMjW2AgoZpSWmJJmmpiaZm5ikJaUuYjUMDvF0UggpSkwpPbz88OJ8heDM3NKcksOL8lITFVIVAhJzUotLihKBwjmpecmZ-cWJABSK5ppdAAAA&mat=CQ-3nlA1P5bX&ved=2ahUKEwiY0o76v_CWAxXrLLkGHaH_A-QQrMcEegQIIBAF', icon: 'ri-google-fill', label: 'Google' },
                ].map((s) => (
                  <li key={s.label}>
                    <a
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      aria-label={`Siga STIB Palestra Silenciosa no ${s.label}`}
                      className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-primary hover:text-white text-gray-300 transition-colors"
                    >
                      <i className={s.icon} aria-hidden="true"></i>
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>
      </div>
    </footer>
  );
}